import { useEffect } from 'react'
import { MediaProgress, useMediaProgressSink } from 'room/common/media-progress'
import { YouTubePlayer, youTubePhase } from './youtube-utils'

/** How often the film's position is read for the report while it plays. */
export const REPORT_EVERY_MS = 200

const read = (player: YouTubePlayer): MediaProgress => {
	const phase = youTubePhase(player.getPlayerState())
	return {
		kind: 'youtube',
		cued: phase === 'cued' || phase === 'unstarted',
		positionMs: Math.round(player.getCurrentTime() * 1000),
		durationMs: Math.round(player.getDuration() * 1000),
		playing: phase === 'playing',
		at: Date.now(),
	}
}

const sameAs = (a: MediaProgress, b: MediaProgress | null): boolean =>
	b?.kind === 'youtube' &&
	a.kind === 'youtube' &&
	a.cued === b.cued &&
	a.playing === b.playing &&
	a.positionMs === b.positionMs &&
	a.durationMs === b.durationMs

/**
 * The film tells the stage's media progress sink (`room/common/media-progress`)
 * what its player does: where it is and whether it moves, read together and
 * stamped with that moment - at once on every change of the player's state,
 * and every `REPORT_EVERY_MS` while anything changed. `playing` is the
 * player's own, not the stream's: a film still waiting for its data is not
 * moving, whatever the trainer asked for, and the console's monitor holds its
 * frame meanwhile. `null` once the player is gone.
 */
export const useReportYouTubeProgress = (
	player: YouTubePlayer | null
): void => {
	const sink = useMediaProgressSink()

	useEffect(() => {
		if (!player) {
			return
		}
		let last: MediaProgress | null = null
		const report = (): void => {
			const progress = read(player)
			if (!sameAs(progress, last)) {
				last = progress
				sink(progress)
			}
		}
		report()
		player.addEventListener('onStateChange', report)
		const reading = window.setInterval(report, REPORT_EVERY_MS)
		return () => {
			window.clearInterval(reading)
			// The player's owner may have destroyed it first (its cleanup runs
			// before this one): it has no frame left then, calls no one, and
			// throws when asked to stop.
			if (player.getIframe()) {
				player.removeEventListener('onStateChange', report)
			}
			sink(null)
		}
	}, [player, sink])
}
